import { Box, Checkbox, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useIntl } from 'react-intl';

import { InfoIconTooltip, TextField, Tooltip } from '@verantos-dev/shared-components';

type FollowUpPeriodRestrictionProps = {
  viewMode: boolean;
  isFollowUpPeriodRestrictionEnabled: boolean;
  setIsFollowUpPeriodRestrictionEnabled: (value: boolean) => void;
  followUpPeriodDaysAfterIndexDate: number | undefined;
  setFollowUpPeriodDaysAfterIndexDate: (value: number | undefined) => void;
  hasIndexDate: boolean;
};

export const FollowUpPeriodRestriction = ({
  viewMode,
  isFollowUpPeriodRestrictionEnabled,
  setIsFollowUpPeriodRestrictionEnabled,
  followUpPeriodDaysAfterIndexDate,
  setFollowUpPeriodDaysAfterIndexDate,
  hasIndexDate,
}: FollowUpPeriodRestrictionProps) => {
  const intl = useIntl();
  const theme = useTheme();

  const onClickFollowUpPeriodRestrictionCheckbox = () => {
    !viewMode &&
      hasIndexDate &&
      setIsFollowUpPeriodRestrictionEnabled(!isFollowUpPeriodRestrictionEnabled);
  };

  const onDaysBeforeIndexDateChange = (_id: string, value: string) => {
    const isNumberOrEmpty = value === '' || /^\d+$/.test(value);
    isNumberOrEmpty &&
      setFollowUpPeriodDaysAfterIndexDate(value === '' ? undefined : parseInt(value));
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
        <Typography sx={{ fontWeight: 500, color: theme.palette.text.primary }} variant="body1">
          {intl.formatMessage({
            id: 'projects.cohortBuilderPage.studyPeriodDialogFollowUpPeriod.title',
          })}
        </Typography>
        <InfoIconTooltip
          id="baselinePeriod"
          width={400}
          title={
            <Box>
              {intl.formatMessage({
                id: 'projects.cohortBuilderPage.studyPeriodDialogFollowUpPeriod.infoLine1',
              })}
              <br />
              <br />
              {intl.formatMessage({
                id: 'projects.cohortBuilderPage.studyPeriodDialogFollowUpPeriod.infoLine2',
              })}
            </Box>
          }
        />
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: theme.spacing(2),
        }}
      >
        <Tooltip
          id="followUpPeriodTitle"
          arrow
          placement="top"
          title={
            hasIndexDate
              ? undefined
              : intl.formatMessage({
                  id: 'projects.cohortBuilderPage.studyPeriodDialogFollowUpPeriodCheckbox.title.disabled',
                })
          }
        >
          <Checkbox
            sx={{
              marginLeft: `-${theme.spacing(3)}`,
              cursor: hasIndexDate ? 'pointer' : 'default',
              color: hasIndexDate ? undefined : theme.palette.text.disabled,
            }}
            aria-checked={isFollowUpPeriodRestrictionEnabled}
            checked={isFollowUpPeriodRestrictionEnabled}
            onClick={onClickFollowUpPeriodRestrictionCheckbox}
            disableRipple={viewMode || !hasIndexDate}
            disableFocusRipple={viewMode || !hasIndexDate}
            disableTouchRipple={viewMode || !hasIndexDate}
          />
        </Tooltip>
        <Typography
          sx={{
            cursor: viewMode ? 'default' : 'pointer',
            color: hasIndexDate ? undefined : theme.palette.text.disabled,
          }}
          variant="body1"
          onClick={onClickFollowUpPeriodRestrictionCheckbox}
        >
          {intl.formatMessage({
            id: 'projects.cohortBuilderPage.studyPeriodDialogFollowUpPeriod.checkboxLabel',
          })}
        </Typography>
      </Box>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          mt: theme.spacing(2),
        }}
      >
        <TextField
          id="followUpPeriodInputField"
          displayLabel={intl.formatMessage({
            id: 'projects.cohortBuilderPage.studyPeriodDialogFollowUpPeriod.followUpEditLabel',
          })}
          placeholder={intl.formatMessage({
            id: 'projects.cohortBuilderPage.studyPeriodDialogBaselinePeriod.baselineEditPlaceholder',
          })}
          type="integer"
          value={followUpPeriodDaysAfterIndexDate?.toString() ?? ''}
          width={parseInt(theme.spacing(35), 10)}
          onChange={onDaysBeforeIndexDateChange}
          disabled={!isFollowUpPeriodRestrictionEnabled || viewMode}
        />
        <Typography
          sx={{
            marginLeft: theme.spacing(4),
            color: isFollowUpPeriodRestrictionEnabled
              ? theme.palette.text.primary
              : theme.palette.text.disabled,
          }}
          variant="body1"
        >
          {intl.formatMessage({
            id: 'projects.cohortBuilderPage.studyPeriodDialogFollowUpPeriod.followUpEditMessage',
          })}
        </Typography>
      </Box>
    </Box>
  );
};
